<script setup lang="ts">
import { computed } from 'vue';
import { Descriptions, DescriptionsItem } from 'ant-design-vue';

interface Props {
  value: unknown;
  size?: 'default' | 'middle' | 'small';
  bordered?: boolean;
}

const properties = withDefaults(defineProps<Props>(), {
  size: 'small',
  bordered: true,
});

const isObject = computed(() => (
  properties.value !== null
    && typeof properties.value === 'object'
    && !Array.isArray(properties.value)
));

const isArray = computed(() => Array.isArray(properties.value));

const displayValue = computed(() => {
  if (properties.value === null) {
    return 'null';
  }
  if (properties.value === undefined) {
    return 'undefined';
  }
  if (typeof properties.value === 'boolean') {
    return properties.value.toString();
  }
  if (typeof properties.value === 'string') {
    return properties.value;
  }
  if (typeof properties.value === 'number') {
    return properties.value.toString();
  }
  return JSON.stringify(properties.value);
});

function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
</script>

<template>
  <div v-if="isObject" class="json-value-object">
    <Descriptions :size="size" :bordered="bordered" :column="1">
      <DescriptionsItem
        v-for="(nestedValue, nestedKey) in value"
        :key="nestedKey"
        :label="formatLabel(nestedKey)"
      >
        <JsonValue :value="nestedValue" :size="size" :bordered="bordered" />
      </DescriptionsItem>
    </Descriptions>
  </div>
  <div v-else-if="isArray" class="json-value-array">
    <ul class="list-disc list-inside">
      <li v-for="(item, index) in value" :key="index" class="mb-1">
        <JsonValue :value="item" :size="size" :bordered="bordered" />
      </li>
    </ul>
  </div>
  <span v-else class="json-value-primitive">
    {{ displayValue }}
  </span>
</template>

<style scoped>
.json-value-object {
  margin: 0;
}

.json-value-array {
  margin: 0;
}

.json-value-primitive {
  word-break: break-word;
}
</style>
